'use client'

import { usePostCreationStore } from '@/lib/stores/postCreationStore';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Typography from '@tiptap/extension-typography';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered } from 'lucide-react';
import { useEffect } from 'react';

export function BlogEditor() {
  const { currentPost, updatePostField } = usePostCreationStore();
  
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Write your blog post content here...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
    ],
    content: currentPost.content || '',
    onUpdate: ({ editor }) => {
      updatePostField('content', editor.getHTML());
    },
    immediatelyRender: false,
  });
  
  // Sync editor content when currentPost changes
  useEffect(() => {
    if (editor && currentPost.content !== editor.getHTML()) {
      editor.commands.setContent(currentPost.content || '');
    }
  }, [editor, currentPost.content]);
  
  const toggleBold = () => {
    editor?.chain().focus().toggleBold().run();
  };
  
  const toggleItalic = () => {
    editor?.chain().focus().toggleItalic().run();
  };
  
  const toggleBulletList = () => {
    editor?.chain().focus().toggleBulletList().run();
  };
  
  const toggleOrderedList = () => {
    editor?.chain().focus().toggleOrderedList().run();
  };
  
  const setLink = () => {
    const url = window.prompt('URL');
    
    if (url === null) {
      return;
    }
    
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          placeholder="Enter blog post title"
          value={currentPost.title || ''}
          onChange={(e) => updatePostField('title', e.target.value)}
          className="text-xl"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="excerpt">Excerpt</Label>
        <Input
          id="excerpt"
          placeholder="Brief summary or introduction"
          value={currentPost.excerpt || ''}
          onChange={(e) => updatePostField('excerpt', e.target.value)}
        />
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Content</Label>
          <div className="flex items-center space-x-1 border rounded-md p-1">
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('bold') ? 'default' : 'ghost'}
              onClick={toggleBold}
              className="h-8 w-8 p-0"
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('italic') ? 'default' : 'ghost'}
              onClick={toggleItalic}
              className="h-8 w-8 p-0"
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('bulletList') ? 'default' : 'ghost'}
              onClick={toggleBulletList}
              className="h-8 w-8 p-0"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('orderedList') ? 'default' : 'ghost'}
              onClick={toggleOrderedList}
              className="h-8 w-8 p-0"
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('link') ? 'default' : 'ghost'}
              onClick={setLink}
              className="h-8 w-8 p-0"
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="border rounded-md p-4 min-h-[300px]">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
} 